import { Alert } from '@mui/material';
import { nationality } from 'further-ui/labels';

import AppDatePicker from 'components/FormElements/AppDatePicker';
import AppSelectBox from 'components/FormElements/AppSelectBox';
import FieldRow from 'components/FormElements/FieldRow';

import AdviserSelect from 'components/AdviserSelect';
import { InvestorFormValues } from 'helpers/investor/investorForm';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import InvestorAddress from './InvestorAddress';
import SwitchField from './SwitchField';
import TextField from './TextField';
import CustomTagSection from 'components/CustomTagSelector';
import { CategoryOptions } from 'components/CustomTagSelector/types';
import { useDuplicateLegalEntityInvestorWarning } from 'hooks/data/investor/useDuplicateLegalEntityInvestorWarning';
import { DuplicateInvestorAlert } from './Common/DuplicateInvestorAlert';

type Props = {
  id?: string;
  emailInUseByOtherAccount: boolean;
  tagCategoryOptions: CategoryOptions;
  tagCategory?: string;
  tagOptions?: Array<string>;
  onCategoryChange: (category: string) => void;
};

const EntityDetails: React.FC<Props> = ({
  id,
  emailInUseByOtherAccount,
  tagCategory,
  tagCategoryOptions,
  tagOptions,
  onCategoryChange,
}) => {
  const { control } = useFormContext<InvestorFormValues>();
  const isDeceased = useWatch({ control, name: 'isDeceased' });
  const isVulnerableClient = useWatch({ control, name: 'isVulnerableClient' });
  const [registeredName, firmId] = useWatch({
    control,
    name: ['registeredName', 'firmId'],
  });

  const { hasWarning, message } = useDuplicateLegalEntityInvestorWarning({
    registeredName,
    firmId,
    ignoreInvestorId: id,
  });

  return (
    <>
      <FieldRow title="Registered name*" centerTitle>
        <TextField name="registeredName" control={control} />
        {hasWarning && <DuplicateInvestorAlert message={message} />}
      </FieldRow>
      <FieldRow title="Registration number*" centerTitle>
        <TextField name="registrationNumber" control={control} />
      </FieldRow>
      <FieldRow title="Registration date*" centerTitle>
        <Controller
          name="registrationDate"
          control={control}
          render={({
            field: { ref, onChange, value, ...rest },
            fieldState: { error },
          }) => (
            <AppDatePicker
              {...rest}
              maxDate={new Date()}
              inputRef={ref}
              error={!!error}
              value={value ?? null}
              helperText={error?.message}
              openOnFocus
              onChange={(date) => {
                onChange(date);
              }}
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Registration state*" centerTitle>
        <Controller
          name="registrationState"
          control={control}
          render={({
            field: { ref, value, ...rest },
            fieldState: { error },
          }) => (
            <AppSelectBox
              {...rest}
              value={value ?? ''}
              data={nationality}
              valueKey="Country"
              labelKey="Country"
              variant="outlined"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FieldRow>
      {/* Address */}
      <InvestorAddress control={control} isLegalEntity />
      <FieldRow title={id ? 'Email address' : 'User email*'} centerTitle>
        <TextField name="email" control={control} />
        {emailInUseByOtherAccount ? (
          <Alert severity="info">
            This email is used with other accounts. If you change it and the new
            email is not in use in the system, the user will be asked to set a
            new password for the email. Their current email will remain in use
            with other accounts.
          </Alert>
        ) : null}
      </FieldRow>
      <FieldRow title="Key contact first name*" centerTitle>
        <TextField name="keyContactFirstName" control={control} />
      </FieldRow>
      <FieldRow title="Key contact surname*" centerTitle>
        <TextField name="keyContactSurname" control={control} />
      </FieldRow>
      <FieldRow title="Key contact phone number*" centerTitle>
        <TextField name="keyContactPhoneNumber" control={control} />
      </FieldRow>
      {id ? (
        <FieldRow title="Date registered*" centerTitle>
          <Controller
            name="registrationDate"
            control={control}
            render={({
              field: { ref, onChange, value, ...rest },
              fieldState: { error },
            }) => (
              <AppDatePicker
                {...rest}
                disabled
                inputRef={ref}
                value={value ?? null}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </FieldRow>
      ) : null}
      <FieldRow title="External investor ID" centerTitle>
        <TextField name="externalInvestorId" control={control} />
      </FieldRow>
      <FieldRow title="Vulnerable client" centerTitle>
        <span>No</span>
        <SwitchField name="isVulnerableClient" control={control} />
        <span>Yes</span>
      </FieldRow>
      {isVulnerableClient && (
        <FieldRow title="Vulnerability notes" labelSpacing={5}>
          <TextField
            name="vulnerabilityNotes"
            control={control}
            multiline
            rows={3}
          />
        </FieldRow>
      )}
      <FieldRow title="Deceased" centerTitle>
        <span>No</span>
        <SwitchField name="isDeceased" control={control} />
        <span>Yes</span>
      </FieldRow>
      {isDeceased && (
        <FieldRow title="Date of death*" centerTitle>
          <Controller
            name="dateOfDeath"
            control={control}
            render={({
              field: { ref, onChange, value, ...rest },
              fieldState: { error },
            }) => (
              <AppDatePicker
                {...rest}
                maxDate={new Date()}
                inputRef={ref}
                error={!!error}
                value={value ?? null}
                helperText={error?.message}
                openOnFocus
                onChange={(date) => {
                  onChange(date);
                }}
              />
            )}
          />
        </FieldRow>
      )}
      <FieldRow title="Requires hard copy reports" centerTitle>
        <span>No</span>
        <SwitchField name="requiresHardCopyReports" control={control} />
        <span>Yes</span>
      </FieldRow>
      <FieldRow title="Will the investor be claiming tax relief?" centerTitle>
        <span>No</span>
        <SwitchField name="isClaimingTax" control={control} />
        <span>Yes</span>
      </FieldRow>
      <FieldRow title="UK remittance taxpayer (BIR)" centerTitle>
        <span>No</span>
        <SwitchField name="isUkRemittanceTaxpayer" control={control} />
        <span>Yes</span>
      </FieldRow>
      <FieldRow title="Custom tag(s)">
        <Controller
          name="customTags"
          control={control}
          render={({ field: { value, onChange } }) => (
            <CustomTagSection
              selectedCategory={tagCategory}
              categoryOptions={tagCategoryOptions}
              onCategorySelect={onCategoryChange}
              tagOptions={tagOptions}
              onChange={(newTags) => {
                onChange(newTags);
              }}
              tags={value ?? []}
            />
          )}
        />
      </FieldRow>
      {!id && (
        <FieldRow title="Default adviser" centerTitle>
          <Controller
            name="defaultAdviserId"
            control={control}
            render={({ field }) => (
              <AdviserSelect
                adviserId={field.value}
                onChange={(adviser) => field.onChange(adviser?._id ?? null)}
                placeholder="Select default adviser"
                fullWidth
                id="defaultAdviserId"
              />
            )}
          />
        </FieldRow>
      )}
    </>
  );
};

export default EntityDetails;
